import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  StickyNote,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Clock,
  User,
  Tag
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'

const AdminNotes = ({ clientId }) => {
  const [isCreating, setIsCreating] = useState(false)
  const [editingNote, setEditingNote] = useState(null)
  const [newNote, setNewNote] = useState({ title: '', content: '', tags: [] })
  const [tagInput, setTagInput] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [noteToDelete, setNoteToDelete] = useState(null)
  const queryClient = useQueryClient()

  const { data: notes = [], isLoading } = useQuery(
    ['admin-notes', clientId],
    () => apiService.get(`/admin-notes/client/${clientId}`).then(res => res.data.notes),
    { enabled: !!clientId, refetchOnWindowFocus: false }
  )

  const createNoteMutation = useMutation(
    (noteData) => apiService.post(`/admin-notes/client/${clientId}`, noteData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-notes', clientId])
        setIsCreating(false)
        setNewNote({ title: '', content: '', tags: [] })
        setTagInput('')
        toast.success('Note created successfully')
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to create note')
    }
  )

  const updateNoteMutation = useMutation(
    ({ noteId, ...noteData }) => apiService.put(`/admin-notes/${noteId}`, noteData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-notes', clientId])
        setEditingNote(null)
        toast.success('Note updated successfully')
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to update note')
    }
  )

  const deleteNoteMutation = useMutation(
    (noteId) => apiService.delete(`/admin-notes/${noteId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-notes', clientId])
        toast.success('Note deleted successfully')
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to delete note')
    }
  )

  const handleCreateNote = () => {
    if (!newNote.title.trim() || !newNote.content.trim()) {
      toast.error('Please fill in both title and content')
      return
    }
    createNoteMutation.mutate(newNote)
  }

  const handleUpdateNote = () => {
    if (!editingNote.title.trim() || !editingNote.content.trim()) {
      toast.error('Please fill in both title and content')
      return
    }
    updateNoteMutation.mutate(editingNote)
  }

  const handleDeleteNote = (note) => {
    setNoteToDelete(note)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    if (noteToDelete) {
      deleteNoteMutation.mutate(noteToDelete.id)
      setShowDeleteConfirm(false)
      setNoteToDelete(null)
    }
  }

  const addTag = (tags, setTags, input, setInput) => {
    const tag = input.trim().toLowerCase()
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag])
      setInput('')
    }
  }

  const removeTag = (tags, setTags, tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove))
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <div className="card card-pad">
        <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
          <div style={{ padding: 8, background: 'var(--warning-soft)', borderRadius: 'var(--radius)' }}>
            <StickyNote style={{ width: 20, height: 20, color: 'var(--warning)' }} />
          </div>
          <span className="font-semibold">Admin Notes</span>
        </div>
        <div className="flex items-center justify-center" style={{ padding: '32px 0' }}>
          <LoadingSpinner size="md" text="Loading notes..." />
        </div>
      </div>
    )
  }

  return (
    <div className="card card-pad">
      <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
        <div className="flex items-center gap-3">
          <div style={{ padding: 8, background: 'var(--warning-soft)', borderRadius: 'var(--radius)' }}>
            <StickyNote style={{ width: 20, height: 20, color: 'var(--warning)' }} />
          </div>
          <span className="font-semibold">Admin Notes</span>
          <span className="text-muted" style={{ fontSize: 13 }}>({notes.length})</span>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setIsCreating(true)} disabled={isCreating}>
          <Plus style={{ width: 16, height: 16, marginRight: 4 }} />
          Add Note
        </button>
      </div>

      {isCreating && (
        <div style={{ marginBottom: 24, padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface-2)' }}>
          <p className="font-medium" style={{ marginBottom: 16 }}>Create New Note</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Title</label>
              <input
                type="text"
                value={newNote.title}
                onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                className="input"
                placeholder="Note title..."
                maxLength={255}
              />
            </div>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Content</label>
              <textarea
                value={newNote.content}
                onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                rows={4}
                className="input"
                placeholder="Note content..."
                maxLength={10000}
                style={{ resize: 'none' }}
              />
            </div>
            <div>
              <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Tags</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {newNote.tags.map((tag, index) => (
                  <span key={index} className="badge badge-accent" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Tag style={{ width: 10, height: 10 }} />
                    {tag}
                    <button onClick={() => removeTag(newNote.tags, (tags) => setNewNote({ ...newNote, tags }), tag)} style={{ marginLeft: 2, color: 'inherit', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                      <X style={{ width: 10, height: 10 }} />
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value.includes(',')) {
                      const tags = value.split(',').map(t => t.trim()).filter(t => t)
                      tags.forEach(tag => addTag(newNote.tags, (tags) => setNewNote({ ...newNote, tags }), tag, () => {}))
                      setTagInput('')
                    } else {
                      setTagInput(value)
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addTag(newNote.tags, (tags) => setNewNote({ ...newNote, tags }), tagInput, setTagInput)
                    }
                  }}
                  style={{ flex: 1, padding: '0 12px', height: 36, background: 'var(--surface)', color: 'var(--text)', outline: 'none', border: 'none', fontSize: 13 }}
                  placeholder="Add tag..."
                  maxLength={50}
                />
                <button
                  type="button"
                  onClick={() => addTag(newNote.tags, (tags) => setNewNote({ ...newNote, tags }), tagInput, setTagInput)}
                  style={{ padding: '0 12px', background: 'var(--surface-2)', color: 'var(--text-soft)', borderLeft: '1px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  <Plus style={{ width: 16, height: 16 }} />
                </button>
              </div>
            </div>
            <div className="flex justify-between" style={{ gap: 8 }}>
              <button className="btn btn-sm" onClick={() => { setIsCreating(false); setNewNote({ title: '', content: '', tags: [] }); setTagInput('') }}>
                <X style={{ width: 14, height: 14, marginRight: 4 }} />
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleCreateNote} disabled={createNoteMutation.isLoading}>
                {createNoteMutation.isLoading ? <LoadingSpinner size="sm" /> : (
                  <><Save style={{ width: 14, height: 14, marginRight: 4 }} />Save Note</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {notes.length > 0 ? (
          notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              isEditing={editingNote?.id === note.id}
              editingNote={editingNote}
              setEditingNote={setEditingNote}
              onUpdate={handleUpdateNote}
              onDelete={handleDeleteNote}
              updateMutation={updateNoteMutation}
              formatDate={formatDate}
              addTag={addTag}
              removeTag={removeTag}
            />
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <StickyNote style={{ width: 48, height: 48, color: 'var(--text-faint)', margin: '0 auto 12px' }} />
            <p className="font-medium" style={{ marginBottom: 4 }}>No admin notes yet</p>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>Create your first note to keep track of important client information.</p>
            {!isCreating && (
              <button className="btn btn-primary btn-sm" onClick={() => setIsCreating(true)}>
                <Plus style={{ width: 14, height: 14, marginRight: 4 }} />
                Add First Note
              </button>
            )}
          </div>
        )}
      </div>

      {showDeleteConfirm && noteToDelete && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-head">
              <h3 className="font-semibold">Delete Note</h3>
            </div>
            <div className="modal-body">
              <p className="text-soft" style={{ fontSize: 14 }}>
                Are you sure you want to delete the note <strong>"{noteToDelete.title}"</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => { setShowDeleteConfirm(false); setNoteToDelete(null) }}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteConfirm} disabled={deleteNoteMutation.isLoading}>
                {deleteNoteMutation.isLoading ? <LoadingSpinner size="sm" /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const NoteCard = ({ note, isEditing, editingNote, setEditingNote, onUpdate, onDelete, updateMutation, formatDate, addTag, removeTag }) => {
  const [tagInput, setTagInput] = useState('')

  const startEditing = () => setEditingNote({ ...note })
  const cancelEditing = () => { setEditingNote(null); setTagInput('') }

  if (isEditing) {
    return (
      <div style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface-2)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Title</label>
            <input
              type="text"
              value={editingNote.title}
              onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
              className="input"
              maxLength={255}
            />
          </div>
          <div>
            <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Content</label>
            <textarea
              value={editingNote.content}
              onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
              rows={4}
              className="input"
              maxLength={10000}
              style={{ resize: 'none' }}
            />
          </div>
          <div>
            <label className="font-medium text-soft" style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Tags</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {editingNote.tags.map((tag, index) => (
                <span key={index} className="badge badge-accent" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Tag style={{ width: 10, height: 10 }} />
                  {tag}
                  <button onClick={() => removeTag(editingNote.tags, (tags) => setEditingNote({ ...editingNote, tags }), tag)} style={{ marginLeft: 2, color: 'inherit', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                    <X style={{ width: 10, height: 10 }} />
                  </button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => {
                  const value = e.target.value
                  if (value.includes(',')) {
                    const tags = value.split(',').map(t => t.trim()).filter(t => t)
                    tags.forEach(tag => addTag(editingNote.tags, (tags) => setEditingNote({ ...editingNote, tags }), tag, () => {}))
                    setTagInput('')
                  } else {
                    setTagInput(value)
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTag(editingNote.tags, (tags) => setEditingNote({ ...editingNote, tags }), tagInput, setTagInput)
                  }
                }}
                style={{ flex: 1, padding: '0 12px', height: 36, background: 'var(--surface)', color: 'var(--text)', outline: 'none', border: 'none', fontSize: 13 }}
                placeholder="Add tag..."
                maxLength={50}
              />
              <button
                type="button"
                onClick={() => addTag(editingNote.tags, (tags) => setEditingNote({ ...editingNote, tags }), tagInput, setTagInput)}
                style={{ padding: '0 12px', background: 'var(--surface-2)', color: 'var(--text-soft)', borderLeft: '1px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                <Plus style={{ width: 16, height: 16 }} />
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-sm" onClick={cancelEditing}>
              <X style={{ width: 14, height: 14, marginRight: 4 }} />Cancel
            </button>
            <button className="btn btn-primary btn-sm" onClick={onUpdate} disabled={updateMutation.isLoading}>
              {updateMutation.isLoading ? <LoadingSpinner size="sm" /> : (
                <><Save style={{ width: 14, height: 14, marginRight: 4 }} />Save Changes</>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}>
          <p className="font-medium" style={{ marginBottom: 6 }}>{note.title}</p>
          <p className="text-soft" style={{ fontSize: 13, marginBottom: 10, whiteSpace: 'pre-wrap' }}>{note.content}</p>
          {note.tags && note.tags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
              {note.tags.map((tag, index) => (
                <span key={index} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, fontSize: 11, background: 'var(--surface-2)', color: 'var(--text-soft)', border: '1px solid var(--border)' }}>
                  <Tag style={{ width: 10, height: 10 }} />{tag}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-4" style={{ fontSize: 11, color: 'var(--text-faint)' }}>
            <span className="flex items-center gap-1"><User style={{ width: 10, height: 10 }} />{note.author_name}</span>
            <span className="flex items-center gap-1"><Clock style={{ width: 10, height: 10 }} />{formatDate(note.created_at)}</span>
            {note.updated_at !== note.created_at && (
              <span>(edited {formatDate(note.updated_at)})</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1" style={{ marginLeft: 12 }}>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={startEditing} title="Edit note">
            <Edit style={{ width: 14, height: 14 }} />
          </button>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onDelete(note)} title="Delete note" style={{ color: 'var(--danger)' }}>
            <Trash2 style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdminNotes
